import { useAuth0 } from '@auth0/auth0-react'
import { ThemeProvider } from '@mui/material'
import React, { useEffect, useState, useRef } from 'react'
import { Outlet, useLocation } from 'react-router'
import PromoModal from '../components/PromoModal'
import CenteredLogoWithLoader from '../components/ui/CenteredLogoWithLoader'
import { usePromoModal } from '../context/PromoModalContext'
import { checkAndClearAuth } from '../helpers/auth-helper'
import {
  getLatLngFromPlaceId,
  getLocation,
  Location as LocationType,
} from '../helpers/location-helper'
import { ONBOARD_PATH_PARAMS, OnboardPage } from '../helpers/onboard-helper'
import {
  clearPendingSaveMenuIntent,
  getPendingSaveMenuIntent,
} from '../helpers/menuScanIntent'
import {
  clearPendingSaveRestaurantIntent,
  getPendingSaveRestaurantIntent,
} from '../helpers/restaurantSaveIntent'
import {
  clearPendingPointsClaimIntent,
  getPendingPointsClaimIntent,
} from '../helpers/pointsClaimIntent'
import {
  clearPendingGroupSessionIntent,
  getPendingGroupSessionIntent,
} from '../helpers/groupSessionIntent'
import { claimAnonReviews, favoriteRestaurant } from '../store/restaurantSlice'
import { claimAnonPoints } from '../services/rewardsService'
import { clearAnonSavedAliases } from '../helpers/anon-saves'
import { claimGroupSession } from '../services/groupSessionService'
import { logEvent } from '../analytics/analytics'
import { LOGIN_ATTEMPT_KEY } from '../hooks/useAuthHelper'
import { getStoredAcquisition } from '../helpers/acquisition'
import {
  PENDING_REFERRAL_CODE_KEY,
  PENDING_REFERRAL_CONTEXT_KEY,
  RESTAURANT_SIGNUP_REFERRAL_CONTEXT,
} from '../helpers/static-constants'
import { ensureAnonId } from '../helpers/anon-id'
import { ROUTES } from '../helpers/routes-helper'
import useAppUtilities from '../hooks/useAppUtilities'
import { saveMenuFromScan } from '../api/menuScanApi'
import { store, useAppSelector } from '../store'
import {
  getUserInfo,
  loginAudit,
  fetchSession,
  selectLogin,
  setAccessToken,
  setLocation,
  setRestaurantLocation,
} from '../store/authSlice'
import {
  selectEater,
  setOpen,
  setMessage,
  setSeverity,
  fetchEaterSavedRestaurants,
  selectSavedRestaurantIds,
  selectSavedRestaurantsLoading,
  selectSavedRestaurantsError,
} from '../store/profileSlice'
import theme from '../theme'
import { UserType } from '../types'
import { errorLog, infoLog } from '../utils/log-helper'
import { fetchChatToken } from '../store/restaurant/messages'
import Onboard from '../pages/influencers/onboard/Onboard'
import {
  selectIsInfluencerModalOpen,
  selectIsInfluencerOnboardModalOpen,
} from '../store/influencer/contentCreator'
import RegisterInfluencerModal from '../pages/influencers/onboard/RegisterInfluencerModal'
import { pingSession } from '../services/sessionService'
import { useRudderPageViews } from '../hooks/useRudderPageViews'

interface Props {
  children?: any
}

/** True when a referral link stashed a code awaiting claim — the signal to skip onboarding. */
const hasPendingReferralCode = (): boolean => {
  try {
    return !!localStorage.getItem(PENDING_REFERRAL_CODE_KEY)
  } catch {
    return false
  }
}

/**
 * True when the pending referral is a RESTAURANT invite — the invitee must onboard as a restaurant
 * (role picker → restaurant onboarding), NOT skip to the eater home like an eater friend-invite.
 */
const isPendingRestaurantReferral = (): boolean => {
  try {
    return (
      localStorage.getItem(PENDING_REFERRAL_CONTEXT_KEY) ===
      RESTAURANT_SIGNUP_REFERRAL_CONTEXT
    )
  } catch {
    return false
  }
}

const BaseLayout: React.FC<Props> = () => {
  const { dispatch, navigate } = useAppUtilities()
  const location = useLocation()
  const loginResponse = useAppSelector(selectLogin)
  const isInfluencerModalOpen = useAppSelector(selectIsInfluencerModalOpen)
  const isInfluencerOnboardModalOpen = useAppSelector(
    selectIsInfluencerOnboardModalOpen,
  )
  const [dataFetched, setDataFetched] = useState(false)
  const {
    isOpen: isPromoModalOpen,
    isMobile: isPromoModalMobile,
    closeModal: closePromoModal,
  } = usePromoModal()
  const eater = useAppSelector(selectEater)
  const savedRestaurantIds = useAppSelector(selectSavedRestaurantIds)
  const savedRestaurantsLoading = useAppSelector(selectSavedRestaurantsLoading)
  const savedRestaurantsError = useAppSelector(selectSavedRestaurantsError)

  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0()

  // Track page views globally (public and authenticated routes)
  useRudderPageViews()

  // Location fetch throttling refs
  const locationFetchInFlightRef = useRef(false)
  const locationFetchedThisBootRef = useRef(false)
  // Captured in fetchData (before loginResponse updates and before the async claimPendingReferral
  // effect clears the code) so handleNavigation can reliably skip onboarding for referral signups.
  const referralSignupRef = useRef(false)
  // Durable across the component's life: a RESTAURANT invite must land on the role picker
  // (restaurant onboarding), never the eater home/onboarding. handleNavigation runs multiple times
  // per login (StrictMode double-invoke, re-dispatched loginAudit) and the one-shot referral block
  // clears its own markers on the first run — so the later `isNewUser` fallback would otherwise
  // re-route the invitee into EATER onboarding. This ref keeps the restaurant-referral intent alive
  // so BOTH the referral block and the isNewUser fallback route to CHOOSE_ROLE.
  const restaurantReferralRef = useRef(false)

  // Location fetch throttle window: 10 minutes
  const LOCATION_FETCH_THROTTLE_MS = 10 * 60 * 1000
  const LAST_LOCATION_FETCH_KEY = 'last_location_fetch_at'

  // Session ping throttling (10 minutes)
  const SESSION_PING_THROTTLE_MS = 10 * 60 * 1000 // 10 minutes
  const LAST_SESSION_PING_KEY = 'last_session_ping_at'

  const canPingSession = (): boolean => {
    const lastPingStr = localStorage.getItem(LAST_SESSION_PING_KEY)
    if (!lastPingStr) return true

    const lastPing = parseInt(lastPingStr, 10)
    const now = Date.now()
    return now - lastPing >= SESSION_PING_THROTTLE_MS
  }

  /**
   * Validate cached location from localStorage
   * Must have numeric latitude and longitude
   */
  const validateCachedLocation = (cached: any): LocationType | null => {
    if (!cached || typeof cached !== 'object') return null

    const lat = cached.latitude ?? cached.lat
    const lng = cached.longitude ?? cached.lng

    if (
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    ) {
      return { latitude: lat, longitude: lng } satisfies LocationType
    }

    return null
  }

  /**
   * Fetch and store location if needed (with throttling and caching)
   *
   * Gating logic:
   * - Skip if already fetched this boot
   * - Skip if fetch is in-flight
   * - Use cached location if valid and within throttle window
   * - Otherwise fetch new location
   */
  const fetchAndStoreLocationIfNeeded = async (): Promise<void> => {
    // Gate 0: Redux already has location (e.g. set by Home); avoid duplicate fetch
    const existingLocation = store.getState().auth.location
    if (
      existingLocation &&
      typeof existingLocation.latitude === 'number' &&
      typeof existingLocation.longitude === 'number'
    ) {
      locationFetchedThisBootRef.current = true
      infoLog('[BaseLayout] Location fetch skipped: already in Redux', {
        lat: existingLocation.latitude,
        lng: existingLocation.longitude,
      })
      return
    }

    // Gate 1: Already fetched this boot
    if (locationFetchedThisBootRef.current) {
      infoLog('[BaseLayout] Location fetch skipped: already fetched this boot')
      return
    }

    // Gate 2: Fetch already in-flight
    if (locationFetchInFlightRef.current) {
      infoLog('[BaseLayout] Location fetch skipped: fetch already in-flight')
      return
    }

    // Gate 3: Check for valid cached location
    try {
      const cachedLocationStr = localStorage.getItem('location')
      if (cachedLocationStr) {
        const cachedLocation = validateCachedLocation(
          JSON.parse(cachedLocationStr),
        )
        if (cachedLocation) {
          // Check throttle window
          const lastFetchStr = localStorage.getItem(LAST_LOCATION_FETCH_KEY)
          if (lastFetchStr) {
            const lastFetch = parseInt(lastFetchStr, 10)
            const now = Date.now()
            const timeSinceLastFetch = now - lastFetch

            if (timeSinceLastFetch < LOCATION_FETCH_THROTTLE_MS) {
              // Within throttle window - use cached location
              infoLog(
                '[BaseLayout] Location fetch skipped: using cached location (within throttle window)',
                {
                  timeSinceLastFetch: `${Math.round(timeSinceLastFetch / 1000)}s`,
                  cached: cachedLocation,
                },
              )
              dispatch(setLocation(cachedLocation))
              locationFetchedThisBootRef.current = true
              return
            }
          } else {
            // No last fetch timestamp - use cached location but don't skip fetch
            // (fall through to fetch to update timestamp)
            infoLog(
              '[BaseLayout] Found cached location but no timestamp - will fetch to update',
              {
                cached: cachedLocation,
              },
            )
            // Still dispatch cached location so app doesn't hang
            dispatch(setLocation(cachedLocation))
          }
        }
      }
    } catch (error) {
      // If localStorage read fails, continue to fetch
      infoLog('[BaseLayout] Error reading cached location, will fetch', error)
    }

    // All gates passed - fetch location
    locationFetchInFlightRef.current = true

    try {
      infoLog('[BaseLayout] Fetching location (gates passed)')
      const location = await getLocation()

      infoLog('[BaseLayout] Location fetched successfully', {
        lat: location.latitude,
        lng: location.longitude,
      })

      dispatch(setLocation(location))
      localStorage.setItem(LAST_LOCATION_FETCH_KEY, Date.now().toString())

      locationFetchedThisBootRef.current = true
    } catch (error) {
      errorLog('[BaseLayout] Location fetch failed', error)

      // Use default location to prevent hang
      const defaultLocation = { latitude: 40.73061, longitude: -73.935242 }
      dispatch(setLocation(defaultLocation))

      // Still mark as fetched to avoid retry loops
      locationFetchedThisBootRef.current = true
    } finally {
      locationFetchInFlightRef.current = false
    }
  }

  const getOnboardRoute = (): string => {
    const stepToPathMap: { [key in OnboardPage]?: string } = {
      [OnboardPage.ALLERGIES]: ONBOARD_PATH_PARAMS.ALLERGIES,
      [OnboardPage.FAVORITES]: ONBOARD_PATH_PARAMS.PERSONAL_FAVORITES,
      [OnboardPage.PREPARATIONS]: ONBOARD_PATH_PARAMS.FOOD_PREPARATIONS,
      [OnboardPage.DINING_FREQUENCY]: ONBOARD_PATH_PARAMS.DINING_FREQUENCY,
      [OnboardPage.INGREDIENTS]: ONBOARD_PATH_PARAMS.INGREDIENTS,
      [OnboardPage.CUISINES]: ONBOARD_PATH_PARAMS.CUISINES,
      [OnboardPage.AMBIENCE]: ONBOARD_PATH_PARAMS.DINING_AMBIENCE,
      [OnboardPage.DIETARY_PREFERENCES]:
        ONBOARD_PATH_PARAMS.DIETARY_PREFERENCE_RESTRICTION,
      [OnboardPage.SOCIAL_MEDIA]: ONBOARD_PATH_PARAMS.SOCIAL,
      [OnboardPage.PERSONALIZE]: ONBOARD_PATH_PARAMS.PERSONALIZE,
      [OnboardPage.LOCATE_YOUR_RESTAURANT]:
        ONBOARD_PATH_PARAMS.LOCATE_YOUR_RESTAURANT,
      [OnboardPage.REQUEST_ONBOARDING_RESTAURANT]:
        ONBOARD_PATH_PARAMS.REQUEST_ONBOARDING_RESTAURANT,
    }

    const step = loginResponse?.eaterDTO?.step
    return stepToPathMap[step ?? ''] || ONBOARD_PATH_PARAMS.CHOOSE_ROLE
  }

  useEffect(() => {
    if (loginResponse) {
      void handleNavigation()
    }
  }, [loginResponse])

  // Allow app (and Home/landing) to render as soon as auth state is known.
  // Do not block on getUserInfo/loginAudit/fetchChatToken; run them in parallel.
  useEffect(() => {
    if (!isLoading) {
      setDataFetched(true)
      if (isAuthenticated) {
        void fetchData()
      } else {
        checkAndClearAuth()
      }
    }
  }, [isLoading, isAuthenticated])

  // Ping session after initial authenticated load (after dataFetched)
  useEffect(() => {
    if (dataFetched && isAuthenticated && !isLoading) {
      if (canPingSession()) {
        localStorage.setItem(LAST_SESSION_PING_KEY, Date.now().toString())
        void pingSession()
      }
    }
  }, [dataFetched, isAuthenticated, isLoading])

  // Session ping on visibility change and focus
  useEffect(() => {
    if (!isAuthenticated) return

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && canPingSession()) {
        localStorage.setItem(LAST_SESSION_PING_KEY, Date.now().toString())
        void pingSession()
      }
    }

    const handleFocus = (): void => {
      if (canPingSession()) {
        localStorage.setItem(LAST_SESSION_PING_KEY, Date.now().toString())
        void pingSession()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [isAuthenticated])

  useEffect(() => {
    const restaurantDTO = loginResponse?.restaurantDTO
    const status = loginResponse?.status
    const type = loginResponse?.type

    const homeRoute =
      `${window.location.pathname}${window.location.search}${window.location.hash}` ===
      ROUTES.HOME
    const aboutRoute =
      `${window.location.pathname}${window.location.search}${window.location.hash}` ===
      ROUTES.ABOUT
    if (
      restaurantDTO &&
      type === UserType.RESTAURANT &&
      status === 'ACTIVE' &&
      (homeRoute || aboutRoute)
    ) {
      navigate(ROUTES.RESTAURANT_ADMIN_DASHBOARD)
    }
  }, [location])

  const handleNavigation = async (): Promise<void> => {
    const status = loginResponse?.status
    const eaterDTO = loginResponse?.eaterDTO
    const restaurantDTO = loginResponse?.restaurantDTO
    const chefDTO = loginResponse?.chefDTO
    const type = loginResponse?.type
    const isNewUser = loginResponse?.isNewUser === true

    infoLog('[BaseLayout] handleNavigation: Starting', {
      status,
      type,
    })

    // Never navigate while Auth0 is still processing the redirect callback. The authorization code +
    // state live in the URL query (`/?code=…&state=…`) and the SDK needs them there to finish the
    // token exchange. Any React Router navigate() here rewrites the URL, strips ?code&state, and
    // aborts the exchange — isAuthenticated stays false, the protected destination bounces to
    // /unauthorized, the Error page re-logs-in, Auth0 returns a fresh code… an endless
    // callback ↔ /unauthorized loop. This bites RETURNING users specifically: a persisted login
    // makes loginResponse truthy on mount, so handleNavigation runs before auth settles. Mirrors the
    // same guard in App.tsx's auth-failure handler. Once the SDK finishes it cleans the URL and
    // re-dispatches loginAudit, so handleNavigation runs again with a clean URL.
    const callbackSearch = window.location.search
    if (callbackSearch.includes('code=') && callbackSearch.includes('state=')) {
      infoLog('[BaseLayout] handleNavigation: deferring — auth callback in progress')
      return
    }

    // Restaurant-referral routing (highest priority). A restaurant invite must land the invitee in
    // restaurant onboarding (the restaurant-info step), never eater onboarding/home. This runs BEFORE the
    // status switch on purpose: login-lite auto-provisions a real EATER with status ACTIVE, so on a
    // repeat login the invitee comes back as an existing ACTIVE eater (isNewUser=false) and would
    // otherwise fall into `case 'ACTIVE' → getOnboardRoute()` (eater onboarding).
    //
    // Two guards keep this from looping. (1) It fires only while a referral code is genuinely
    // pending — NOT off a stale `pendingReferralContext` key left in localStorage by an earlier
    // test — so a normal eater is never yanked into onboarding. (2) It only redirects FROM a
    // non-onboarding page: once the invitee is inside /onboard, the onboarding flow (Onboard.tsx)
    // and their role selection own navigation. Without this, BaseLayout (fires on every
    // loginResponse change) and Onboard.tsx's own step-restore effect ping-pong — the redirect
    // loop. Defer entirely once the role is resolved (eater step DONE, or already a restaurant).
    // Route on the DURABLE restaurant-referral CONTEXT, which claimPendingReferral now KEEPS (only
    // the code is cleared) until restaurant onboarding completes. Gating this on hasPendingReferralCode()
    // dropped the invitee to home when the claim won the race and cleared the code. The ref is a
    // belt-and-suspenders capture from before any clear.
    const restaurantReferralPending =
      restaurantReferralRef.current || isPendingRestaurantReferral()
    const alreadyResolvedRole =
      eaterDTO?.step === 'DONE' ||
      (type === UserType.RESTAURANT && !!restaurantDTO)
    const onOnboardingRoute = window.location.pathname.startsWith(
      `${ROUTES.ONBOARD}/`,
    )
    if (restaurantReferralPending && !alreadyResolvedRole) {
      // A pending restaurant referral OWNS navigation. Route to the first restaurant-info step when
      // the invitee isn't already inside onboarding (Onboard presets role='restaurant' and owns the
      // step machinery from there); otherwise leave them where they are. Either way RETURN — never
      // fall through to the eater intent-resume / referral→home / isNewUser→role-picker branches
      // below. handleNavigation re-runs on every loginResponse change; on a re-run the invitee is
      // already on /onboard/locate-your-restaurant, so the old `!onOnboardingRoute` guard let the
      // flow fall through to the eater-referral→home branch and yanked them to home (first load →
      // home; only a manual refresh — a single clean run — recovered). restaurantReferralRef stays
      // true across the claim even if the context is cleared, so re-runs stay put.
      if (!onOnboardingRoute) {
        navigate(`${ROUTES.ONBOARD}/${ONBOARD_PATH_PARAMS.LOCATE_YOUR_RESTAURANT}`)
      }
      return
    }

    // Post-auth resume: pending save menu intent (bypass onboarding, run save, go to success)
    if (type === UserType.EATER) {
      // Claim any anonymous reviews + provisional anon points + guest-eater dinner sessions left on
      // this device before signup (all idempotent, fire-and-forget). Capture the pending group
      // session's share token first (before the intent is consumed below) so session_claimed can be
      // attributed to the session.
      const groupIntentForClaim = getPendingGroupSessionIntent()
      void ensureAnonId().then((anonId) => {
        if (anonId) {
          // Promise.resolve guards against a non-thenable dispatch return (mocked in tests).
          void Promise.resolve(dispatch(claimAnonReviews(anonId))).then((result) => {
            // Instrument the anon->auth review loop: fire review_claimed only when reviews were
            // actually linked (backend returns { claimed: <n> }), so the claim step is measurable.
            if (result && claimAnonReviews.fulfilled.match(result)) {
              const claimed = Number(result.payload?.claimed ?? 0)
              if (claimed > 0) {
                logEvent('review_claimed', {
                  count: claimed,
                  screen_name: 'auth_resume',
                })
              }
            }
          })
          void claimAnonPoints(anonId).then((res) => {
            // Emit points_earned only when the claim actually credited the (deduped) welcome bonus
            // on this call — the synchronous credit here. Review points are credited by the
            // review-claim pipeline and instrumented on their own path.
            if (res?.welcomeBonusGranted) {
              const bonus =
                res.lineItems?.find((l) => l.reason === 'ANON_WELCOME_BONUS')
                  ?.points ?? 100
              logEvent('points_earned', {
                points_amount: bonus,
                reason: 'anon_welcome_bonus_claim',
                screen_name: 'signup',
              })
            }
            // Anon-first saves: the claim just promoted the device's anonymous saves into real
            // favorites server-side. Announce the merge, retire the device-local alias mirror
            // (the account's favorites are the source of truth now), and refresh the saved ids
            // so bookmark icons flip to the account state without a reload.
            if ((res?.savedRestaurantsMerged ?? 0) > 0) {
              logEvent('restaurant_save_claimed', {
                saves_merged: res?.savedRestaurantsMerged ?? 0,
                screen_name: 'signup',
              })
              clearAnonSavedAliases()
              void dispatch(
                fetchEaterSavedRestaurants({
                  eaterId: loginResponse?.eaterDTO?.eaterId ?? '',
                }),
              )
            } else if (res) {
              // Claim ran with nothing to merge - clear any stale local mirror anyway.
              clearAnonSavedAliases()
            }
          })
          // Dedup the group claim per-anon on this device. handleNavigation re-runs on every
          // loginResponse change (StrictMode double-invoke, silent token refresh, re-dispatched
          // loginAudit); without this guard the claim (idempotent server-side) re-fired and emitted
          // a fresh `session_claimed` each time — inflating the viral-loop "Claimed" step 100x+ with
          // null-token rows. Claim + emit exactly once per anon.
          const groupClaimKey = `rsly_group_claimed:${anonId}`
          let alreadyClaimedGroup = false
          try {
            alreadyClaimedGroup = localStorage.getItem(groupClaimKey) === '1'
          } catch {
            // localStorage unavailable — fall through and attempt the claim
          }
          if (!alreadyClaimedGroup) {
            void claimGroupSession(anonId, groupIntentForClaim?.shareToken)
              .then((res) => {
                if (res?.claimed) {
                  try {
                    localStorage.setItem(groupClaimKey, '1')
                  } catch {
                    // best-effort dedup marker
                  }
                  logEvent('session_claimed', {
                    share_token: groupIntentForClaim?.shareToken,
                    merged_memberships: res.mergedMemberships,
                    merged_owned_sessions: res.mergedOwnedSessions,
                    screen_name: 'post_signup_resume',
                  })
                }
              })
              .catch(() => {
                // claim is best-effort; never break auth
              })
          }
        }
      })
      const pendingSave = getPendingSaveMenuIntent()
      if (pendingSave) {
        clearPendingSaveMenuIntent()
        try {
          await saveMenuFromScan(pendingSave.scanId, {
            restaurantId: pendingSave.restaurantId ?? undefined,
            menuName: 'Scanned Menu',
            dishes: pendingSave.dishes,
          })
          navigate(ROUTES.MENU_SAVE_SUCCESS)
          return
        } catch (e) {
          errorLog('[BaseLayout] Resume save menu failed', e)
          navigate(ROUTES.MENU_SCAN_RESULTS, {
            state: {
              scanId: pendingSave.scanId,
              result: {
                scanId: pendingSave.scanId,
                restaurantId: pendingSave.restaurantId ?? null,
                status: 'COMPLETED',
                message: null,
                dishes: pendingSave.dishes,
              },
              saveError: 'Could not save menu. Please try again.',
            },
          })
          return
        }
      }

      // Post-auth resume: pending save-restaurant intent (run the save, return to origin page).
      const pendingSaveRestaurant = getPendingSaveRestaurantIntent()
      if (pendingSaveRestaurant) {
        clearPendingSaveRestaurantIntent()
        try {
          const result = await dispatch(
            favoriteRestaurant({
              alias: pendingSaveRestaurant.alias,
              name: pendingSaveRestaurant.name,
            }),
          )
          if (favoriteRestaurant.fulfilled.match(result)) {
            logEvent('restaurant_saved', {
              auth_state: 'authed',
              restaurant_id: pendingSaveRestaurant.restaurantId ?? '',
              recommendation_id: pendingSaveRestaurant.recommendationId,
              screen_name: 'post_signup_resume',
            })
          }
        } catch (e) {
          errorLog('[BaseLayout] Resume save restaurant failed', e)
        }
        navigate(pendingSaveRestaurant.returnRoute)
        return
      }

      // Post-auth resume: pending anon-points claim intent. The claim itself runs unconditionally
      // above (idempotent); consuming the intent lands the user on the points page and returns
      // early — which bypasses onboarding, exactly like the save-restaurant / menu-scan resumes.
      const pendingPointsClaim = getPendingPointsClaimIntent()
      if (pendingPointsClaim) {
        clearPendingPointsClaimIntent()
        navigate(pendingPointsClaim.returnRoute)
        return
      }

      // Post-auth resume: pending group-dining-session intent. The guest-eater claim runs
      // unconditionally above (idempotent); consuming the intent lands the user back in the session
      // and returns early — which bypasses onboarding, exactly like the save / points resumes.
      const pendingGroupSession = getPendingGroupSessionIntent()
      if (pendingGroupSession) {
        clearPendingGroupSessionIntent()
        navigate(pendingGroupSession.returnRoute)
        return
      }

      // Post-auth resume: referral-link signup. A referred user arrived because a friend vouched —
      // front-loading full onboarding is the highest-friction activation moment, so we skip it and
      // land them on home (matching the group-session invite flow). The referral code is claimed
      // independently in the claimPendingReferral effect, and the backend stamped the REFERRAL
      // acquisition channel (step DONE) so they are not re-nagged to onboard on later logins. The
      // ProfileCompletionBanner on home is the soft nudge to finish their profile.
      // Eater referral (a friend vouched) → skip onboarding, land on home. Restaurant referrals are
      // handled earlier by the top-of-function role-picker guard and never reach here. The context
      // key is cleared by the claimPendingReferral effect once the referral is actually claimed.
      if (referralSignupRef.current || hasPendingReferralCode()) {
        referralSignupRef.current = false
        navigate(ROUTES.HOME)
        return
      }

      // A brand-new eater that reached this point did NOT resume a scan / save / points / group /
      // referral intent above — i.e. they came in through the plain login/signup button. Always
      // send them to onboarding, regardless of the onboarding step the backend stamped: a stale
      // flow flag left in sessionStorage from an earlier abandoned flow can poison the acquisition
      // channel to a skip-onboarding one (step 'DONE'), which would otherwise route a new user to
      // home. isNewUser is the authoritative "this login just created the account" signal.
      if (isNewUser) {
        // A plain new signup has NOT chosen a role yet, so send them to the role picker — NOT the
        // backend-stamped eater step. login-lite provisions every new principal as an ACTIVE EATER
        // at PERSONALIZE (the "John Smith" fix), so getOnboardRoute() would resolve to eater
        // onboarding and silently drop restaurant owners into the eater flow, never showing them
        // "choose your role". Restaurant referrals were already routed to restaurant onboarding by
        // the top-of-function guard and returned above, so they never reach here.
        navigate(`${ROUTES.ONBOARD}/${ONBOARD_PATH_PARAMS.CHOOSE_ROLE}`)
        return
      }
    }

    // Fetch location with throttling/gating
    await fetchAndStoreLocationIfNeeded()

    switch (status) {
      case 'ACTIVE':
        if (eaterDTO?.step === 'DONE' && type === UserType.EATER) {
          // Fully-onboarded eater — do NOT navigate. Leave them where they landed (post-login home,
          // or the page they clicked to). The previous `navigate(currentPath)` re-fired on every
          // loginResponse change (StrictMode double-invoke, silent token refresh, re-dispatched
          // loginAudit) and snapped users to stale pages — e.g. back to a restaurant detail page
          // while navigating to Points. A no-op here is the correct behavior for an active eater.
        } else if (restaurantDTO && type === UserType.RESTAURANT) {
          if (
            `${window.location.pathname}${window.location.search}${window.location.hash}` ===
            ROUTES.HOME
          ) {
            navigate(ROUTES.RESTAURANT_ADMIN_DASHBOARD)
          } else {
            navigate(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            )
          }
        } else if (chefDTO) {
          navigate(ROUTES.CHEF_DASHBOARD)
        } else {
          navigate(`${ROUTES.ONBOARD}/${getOnboardRoute()}`)
        }
        break
      case 'TRIAL':
      case 'SUBSCRIBED':
        navigate(`${ROUTES.ONBOARD}/${getOnboardRoute()}`)
        break
      case 'NEW':
        if (!eaterDTO) {
          navigate(`${ROUTES.ONBOARD}/${ONBOARD_PATH_PARAMS.CHOOSE_ROLE}`)
        }
        break
      case 'PENDING':
        navigate(ROUTES.RESTAURANT_ADMIN_PENDING)
        break
      case 'INACTIVE':
        navigate(ROUTES.UNAUTHORIZED)
        break
      case 'DELETED':
        navigate(ROUTES.RECOVER_ACCOUNT)
        break
      case 'BLOCKED':
        navigate(ROUTES.RECOVER_BLOCKED_ACCOUNT)
        break
      default:
        break
    }
  }

  const fetchData = async (): Promise<void> => {
    try {
      const token = await getAccessTokenSilently()
      localStorage.setItem('isUserLoggedIn', 'true')
      dispatch(setAccessToken(token))
      await dispatch(getUserInfo())

      // Await login-lite for routing decisions.
      // Pass the acquisition-channel flags so the backend can skip onboarding for menu-scan,
      // save-restaurant, group-session-claim and points-claim flows (and route normal signups
      // through onboarding). The pending intents are still in sessionStorage here —
      // handleNavigation consumes/clears them post-response.
      const hasPendingScan = !!getPendingSaveMenuIntent()
      const hasPendingSave = !!getPendingSaveRestaurantIntent()
      const hasPendingGroupSession = !!getPendingGroupSessionIntent()
      const hasPendingPointsClaim = !!getPendingPointsClaimIntent()
      const hasPendingReferral = !!hasPendingReferralCode()
      const restaurantReferral = isPendingRestaurantReferral()
      if (hasPendingReferral) {
        referralSignupRef.current = true
        // Durable restaurant-invite marker so every handleNavigation run this login routes the
        // invitee to the role picker, not eater onboarding (set here — before loginAudit fires
        // handleNavigation — and never cleared until the referral is claimed).
        restaurantReferralRef.current = restaurantReferral
      }
      const res = await dispatch(
        loginAudit({
          menuScanFlow: hasPendingScan,
          saveFlow: hasPendingSave,
          groupSessionFlow: hasPendingGroupSession,
          pointsClaimFlow: hasPendingPointsClaim,
          // Only an EATER invite skips onboarding; a restaurant invite must run restaurant
          // onboarding (role picker), so it is NOT flagged as a skip-onboarding channel.
          referralFlow: hasPendingReferral && !restaurantReferral,
        }),
      )

      // Handle restaurant location fetch (if placeId is in lite response)
      if (
        loginAudit.fulfilled.match(res) &&
        res.payload.type === 'RESTAURANT'
      ) {
        await dispatch(fetchChatToken('RESTAURANT'))
        if (
          res.payload?.restaurantDTO?.placeId &&
          res.payload?.restaurantDTO?.placeId.length > 0
        ) {
          const location = await getLatLngFromPlaceId(
            res.payload?.restaurantDTO?.placeId,
          )
          dispatch(setRestaurantLocation(location))
        }
      }
      if (loginAudit.fulfilled.match(res) && res.payload.type === 'EATER') {
        await dispatch(fetchChatToken('EATER'))
        // Authoritative account_created: the backend sets isNewUser only on the login that just
        // provisioned the account. Dedup via localStorage so it can never double-fire.
        if (res.payload?.isNewUser === true) {
          try {
            const newEaterId: string | undefined =
              res.payload?.eaterDTO?.eaterId
            const SEEN_EATERS_KEY = 'rsly_seen_eaters'
            const seen: string[] = JSON.parse(
              localStorage.getItem(SEEN_EATERS_KEY) ?? '[]',
            )
            if (!newEaterId || !seen.includes(newEaterId)) {
              if (newEaterId) {
                seen.push(newEaterId)
                localStorage.setItem(
                  SEEN_EATERS_KEY,
                  JSON.stringify(seen.slice(-200)),
                )
              }
              const acq = getStoredAcquisition()
              // A brand-new account provisioned while a group-session claim was pending is a viral /
              // group acquisition, not a direct signup — tag the source so the acquisition funnel
              // counts it while paid-CVR analysis can still filter it out. Requires the backend to
              // return isNewUser=true when a guest is claimed into a freshly-provisioned account.
              logEvent('account_created', {
                screen_name: window.location.pathname || 'unknown',
                source: hasPendingGroupSession ? 'group_session_claim' : 'signup_modal',
                utm_source: acq?.utm_source,
                utm_campaign: acq?.utm_campaign,
                gclid: acq?.gclid,
              })
            }
          } catch {
            // analytics must never break auth
          }
        } else {
          // Returning user completing an actual login attempt. The flag (set at login_started)
          // is absent on silent token reloads, so this stays a true login signal, not a
          // page-load counter.
          try {
            if (sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) {
              logEvent('login_completed', {
                duration_ms: 0,
                screen_name: window.location.pathname || 'unknown',
              })
            }
          } catch {
            // analytics must never break auth
          }
        }
        try {
          sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
        } catch {
          // ignore
        }
      }

      // Allow UI to render with skeletons while session hydrates
      setDataFetched(true)

      // Start session hydration in background (don't await - let it run in parallel)
      // This populates lite eaterDTO fields (eaterId, step, subscribed, imageUrl, status)
      // Heavy collections (reviews, restaurantReviews, predictedSavedRestaurants) are NOT included
      void dispatch(fetchSession()).catch((error) => {
        // Session fetch is non-critical for initial render
        // Errors are logged but don't block UI
        errorLog('Session hydration failed (non-critical)', error)
      })
    } catch (error) {
      errorLog('Error fetching data:', error)
      // Only a genuine login attempt (flagged at login_started) counts as a login failure.
      try {
        if (sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) {
          logEvent('login_failed', {
            error_reason: error instanceof Error ? error.message : 'unknown',
            screen_name: window.location.pathname || 'unknown',
          })
          sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
        }
      } catch {
        // analytics must never break auth
      }
    }
  }

  // Claim pending referral code after login and eater profile is loaded
  useEffect(() => {
    const claimPendingReferral = async (): Promise<void> => {
      const { PENDING_REFERRAL_CODE_KEY, PENDING_REFERRAL_CONTEXT_KEY } =
        await import('../helpers/static-constants')
      const { persistReferralAttribution } =
        await import('../helpers/referral-attribution-helper')
      const pendingCode = localStorage.getItem(PENDING_REFERRAL_CODE_KEY)

      // Only claim if we have a code AND eater is loaded (has eaterId)
      if (!pendingCode || !eater?.eaterId) return

      // Capture the restaurant-referral intent durably BEFORE the claim clears the context below.
      // The routing guard reads this ref, so if the claim wins the race against handleNavigation the
      // ref keeps the invitee headed to restaurant onboarding instead of falling through to home.
      if (isPendingRestaurantReferral()) {
        restaurantReferralRef.current = true
      }

      infoLog('[Referral] Claiming pending referral', {
        eaterId: eater.eaterId,
        codePrefix: pendingCode.substring(0, 8) + '...',
      })

      try {
        const { claimReferral } = await import('../services/referralsService')
        const claimResponse = await claimReferral(pendingCode)

        // Persist attribution if we have referrer IDs
        persistReferralAttribution(claimResponse, pendingCode)

        // Clear the pending code on success. For a RESTAURANT referral, KEEP the context: the
        // restaurant-onboarding routers (this guard + Onboard.tsx) still need it to send the invitee
        // into restaurant onboarding, and the claim can win the race against that navigation. Onboard
        // clears the context once restaurant onboarding completes.
        localStorage.removeItem(PENDING_REFERRAL_CODE_KEY)
        if (!isPendingRestaurantReferral()) {
          localStorage.removeItem(PENDING_REFERRAL_CONTEXT_KEY)
        }

        infoLog('[Referral] Referral claimed successfully', {
          eaterId: eater.eaterId,
          referralId: claimResponse.referralId,
          context: claimResponse.context,
          hasReferrerEaterId: !!claimResponse.referrerEaterId,
          hasReferrerRestaurantId: !!claimResponse.referrerRestaurantId,
        })

        // Funnel bottom: the backend confirmed the referral and attributed it. K-factor =
        // referral_confirmed / referral_link_shared.
        logEvent('referral_confirmed', {
          context: claimResponse.context,
          referral_id: claimResponse.referralId,
          has_referrer_eater: !!claimResponse.referrerEaterId,
          has_referrer_restaurant: !!claimResponse.referrerRestaurantId,
        })

        // Show success toast (using existing snackbar system)
        dispatch(setOpen(true))
        dispatch(setMessage('Invite applied!'))
        dispatch(setSeverity('success'))
      } catch (error: any) {
        // On error (404, already claimed, invalid, self-referral, etc.), clear the pending code
        // + context. This prevents infinite loops and handles all error cases gracefully.
        localStorage.removeItem(PENDING_REFERRAL_CODE_KEY)
        localStorage.removeItem(PENDING_REFERRAL_CONTEXT_KEY)
        // Do NOT persist attribution on error
        errorLog('[Referral] Failed to claim referral', {
          eaterId: eater.eaterId,
          codePrefix: pendingCode.substring(0, 8) + '...',
          status: error.response?.status,
          error,
        })
      }
    }

    void claimPendingReferral()
  }, [eater?.eaterId, dispatch])

  // Preload saved restaurants on authenticated app load
  useEffect(() => {
    const SAVED_RESTAURANTS_COOLDOWN_MS = 10 * 60 * 1000 // 10 minutes
    const LAST_SAVED_RESTAURANTS_FETCH_KEY = 'last_saved_restaurants_fetch_at'

    const shouldFetchSavedRestaurants = (): boolean => {
      // Only fetch if authenticated
      if (!isAuthenticated || isLoading) return false

      // Only fetch if eaterId exists
      if (!eater?.eaterId) return false

      // Only fetch if savedRestaurantIds is empty (not yet loaded)
      if (savedRestaurantIds.length > 0) return false

      // Don't fetch if already loading
      if (savedRestaurantsLoading) return false

      // Don't fetch if there's a recent error (avoid retry loops)
      if (savedRestaurantsError) return false

      // Check cooldown: only fetch if last fetch was more than 10 minutes ago
      const lastFetchStr = localStorage.getItem(
        LAST_SAVED_RESTAURANTS_FETCH_KEY,
      )
      if (lastFetchStr) {
        const lastFetch = parseInt(lastFetchStr, 10)
        const now = Date.now()
        if (now - lastFetch < SAVED_RESTAURANTS_COOLDOWN_MS) {
          return false
        }
      }

      return true
    }

    if (shouldFetchSavedRestaurants()) {
      localStorage.setItem(
        LAST_SAVED_RESTAURANTS_FETCH_KEY,
        Date.now().toString(),
      )
      void dispatch(
        fetchEaterSavedRestaurants({ eaterId: eater?.eaterId ?? '' }),
      )
    }
  }, [
    isAuthenticated,
    isLoading,
    eater?.eaterId,
    savedRestaurantIds.length,
    savedRestaurantsLoading,
    savedRestaurantsError,
    dispatch,
  ])

  // Old trigger logic removed - now handled by useEarlySignupPopupTrigger hook in Home.tsx
  // This ensures the popup triggers earlier (2 seconds or 150px scroll) on the landing page

  const onPromoClose = (
    reason: 'close_button' | 'backdrop' | 'escape' | 'unknown' = 'unknown',
  ): void => {
    const DISMISSED_REWARDS_POPUP_KEY = 'signup_popup_last_dismissed_at'

    // Store dismissal timestamp
    localStorage.setItem(DISMISSED_REWARDS_POPUP_KEY, Date.now().toString())

    // Close modal via context
    // Note: Dismissed event is tracked in PromoModal before calling onClose
    closePromoModal()
  }

  return (
    <ThemeProvider theme={theme}>
      {(isLoading || !dataFetched) && <CenteredLogoWithLoader />}
      {dataFetched && <Outlet />}
      {isPromoModalOpen && (
        <PromoModal
          onClose={onPromoClose}
          type={isPromoModalMobile ? 'mobile' : 'web'}
        />
      )}
      {isInfluencerModalOpen && <RegisterInfluencerModal />}
      {isInfluencerOnboardModalOpen && <Onboard />}
    </ThemeProvider>
  )
}

export default BaseLayout
